import { connect } from 'react-redux';
import { getBlogDetailStack } from 'features/blog/detail/actions';
import { onEntryChange } from 'utilities/contentStack';
import _ from 'lodash';
import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useInView } from 'react-intersection-observer';
import loadable, { lazy } from '@loadable/component';
import PrerenderSafeLazyLoad from 'components/PrerenderSafeLazyLoad';

import Grid from '@material-ui/core/Grid';
import Helmet from 'react-helmet';
import MetaTags from 'components/MetaTags';
import SwiftypeTags from 'components/SwiftypeTags';

import PostHeader from './elements/PostHeader';
import PaddedDivider from 'components/PaddedDivider';
const BlockContent = lazy(() => import('components/BlockContent'));
const LegacyPostContent = lazy(() => import('./elements/LegacyPostContent'));

const SocialShare = loadable(() => import('components/SocialShare'));
const Container = loadable(() => import('components/Container'));
const TableOfContents = loadable(() => import('components/TableOfContents'));
const StructuredData = loadable(() => import('components/StructuredData'));
const SectionNavigation = loadable(() =>
  import('components/SectionNavigation')
);
const RelatedBlogPosts = lazy(() => import('../elements/RelatedBlogPosts'));
const PostDownload = loadable(() => import('./elements/PostDownload'));
const PostInlineForm = loadable(() => import('./elements/PostInlineForm'));
const PostFooter = lazy(() => import('./elements/PostFooter'));

const PostMain = styled.main`
  > *:first-child {
    margin-top: 0;
  }
  min-height: 102vh;
`;

const BlogDetail = React.memo(
  ({ stack, match, getBlogDetailStack, blogBase }) => {
    const [ref, inView] = useInView({
      initialInView: true
    });

    useEffect(() => {
      getBlogDetailStack(match.url);
    }, [getBlogDetailStack, match.url]);

    useEffect(() => {
      onEntryChange(() => {
        getBlogDetailStack(match.url);
      });
    }, [getBlogDetailStack, match.url]);

    return (
      <>
        <MetaTags stack={stack} />
        <SwiftypeTags
          stack={stack}
          title={stack.title}
          excerpt={stack.excerpt}
          publishedAt={stack.date}
          contentType="Blog"
          image={_.get(stack.hero_image, 'url')}
          tags={(stack.blog_category || []).map(cat => cat.title)}
        />
        <StructuredData
          type="BlogPosting"
          headline={stack.title}
          image={[_.get(stack.hero_image, 'url')]}
          datePublished={stack.date}
          dateModified={stack.date_updated}
        />

        {stack.word_count && (
          <Helmet>
            <meta name="twitter:label1" value="Time to Read" />
            <meta
              name="twitter:data1"
              value={`${Math.ceil(stack.word_count / 200)} min`}
            />
          </Helmet>
        )}

        {_.get(stack.featured_card, 'show') && (
          <Helmet>
            <meta
              name="twitter:label2"
              value={_.get(stack.featured_card, 'link.title')}
            />
            <meta
              name="twitter:data2"
              value={_.get(stack.featured_card, 'link.href')}
            />
          </Helmet>
        )}
        <article>
          <div ref={ref}>
            {!inView && (
              <SectionNavigation
                hide={inView}
                logo={blogBase.header?.logo?.url}
                {...stack}
              />
            )}
            <PostHeader
              featured={_.get(stack.featured_card, 'show')}
              {...stack}
            />
            <Container>
              {stack.show_toc && (
                <TableOfContents {...stack} {...stack?.$?.show_toc} />
              )}

              {_.get(stack.download, 'show') ||
              _.get(stack.inline_form, 'show') ? (
                <>
                  {_.get(stack.download, 'show') && (
                    <PostDownload {...stack} {...stack?.$?.download} />
                  )}
                  {_.get(stack.inline_form, 'show') && (
                    <PostInlineForm
                      {...stack.inline_form}
                      {...stack?.$?.inline_form}
                    />
                  )}
                </>
              ) : (
                <PaddedDivider spacing={2} />
              )}
            </Container>
          </div>
          <Container>
            <Grid container>
              <Grid item md={1} xs={12}>
                <SocialShare
                  url={window.location.href}
                  justify="center"
                  top="84px"
                />
              </Grid>
              <Grid item lg={9} md={10} xs={12}>
                <PostMain>
                  {stack.enhanced_layout ? (
                    <BlockContent sections={stack.sections} />
                  ) : (
                    <LegacyPostContent {...stack} />
                  )}
                </PostMain>
                <PostFooter {...stack} />
                <PaddedDivider hide />
              </Grid>
            </Grid>
          </Container>
        </article>
        <PrerenderSafeLazyLoad>
          {stack.blog_category[0] && stack.blog_category[0].uid && (
            <RelatedBlogPosts category={stack.blog_category[0].uid} show={8} />
          )}
        </PrerenderSafeLazyLoad>
      </>
    );
  }
);

export default connect(
  state => ({
    stack: state.blogDetail.stack,
    loading: state.blogDetail.loading,
    blogBase: state.blogBase.stack
  }),
  { getBlogDetailStack }
)(BlogDetail);
